/**
 * Created by Florin David on 09/01/2017.
 */
import env from 'config/env'
import localStorage from './local-storage'
import appVersion from './version'
import browserSessionId from 'lib/browser-sessionId'
import sessionId from 'lib/sessionId'
import { getLocale } from 'selectors/intl'
import { getToken } from 'selectors/auth'
import { isDemoMode } from 'lib/is-demo'
import apiClient from 'lib/api-client'
import { getState } from 'lib/store'

const environment = env.NODE_ENV
const app = env.LOGGER_APP
const module = env.LOGGER_MODULE
const site = env.LOGGER_SITE
const url = env.LOGGER_URL

enum LogLevel {
  info = 'info',
  debug = 'debug',
  error = 'error',
}

interface LogParams {
  level: LogLevel
  message: string
  params?: Record<string, unknown>
  offline?: boolean
  version?: string
  sendQueuedMessages?: boolean
}

interface UserData {
  email: string
}

interface TenantData {
  name: string
}

export interface LogObject {
  level: LogLevel
  message: string
  offline: boolean
  version: string
  module: string
  site: string
  environment: string
  createdAt: string
  app: string
  user?: UserData
  tenant?: TenantData
  client: {
    userAgent: string
  }
  params: {
    browserSessionId: string
    sessionId: string
    location?: RouterLocation
    language: string
    state?: string
  }
}

const getUserData = (): UserData | undefined => {
  return undefined
}

const getTenantData = (): TenantData | undefined => {
  return undefined
}

/**
 * Add message to the queue
 */
const addLogToQueue = (logObject: LogObject): void => {
  const queue: LogObject[] = (localStorage.get('logs') || []) as LogObject[]
  queue.push({ ...logObject, offline: true })
  localStorage.set('logs', queue)
}

/**
 * Clear cache - delete logs from local storage
 */
export const clearCache = (): void => {
  localStorage.clear('logs')
}

/**
 * Send queued messages (unset logs)
 */
export const sendQueuedLogs = async (): Promise<void> => {
  const queue = (localStorage.get('logs') || []) as LogObject[]
  clearCache()

  for (const logObject of queue) {
    /* eslint-disable @typescript-eslint/no-use-before-define */
    await log(logObject)
    /* eslint-enable @typescript-eslint/no-use-before-define */
  }
}

/**
 * Build log object from params
 */
const buildLogObject = (logParams: LogParams): LogObject => {
  const {
    level,
    message,
    params = {},
    offline = false,
    version = appVersion,
  } = logParams

  const state = getState()

  return {
    level,
    module,
    site,
    environment,
    message,
    app,
    version,
    offline,
    createdAt: new Date().toISOString(),
    user: getUserData(),
    tenant: getTenantData(),
    client: {
      userAgent: navigator.userAgent,
    },
    params: {
      browserSessionId,
      sessionId,
      location: {
        pathname: window.location.hash.split('?')[0],
        search: window.location.hash.split('?')[1],
      },
      language: getLocale(),
      state:
        level === 'error'
          ? JSON.stringify({
              ...state,
              // remove messges from logs
              intl: {
                ...state.intl,
                messages: [],
              },
              user: {
                ...state.auth,
                token: undefined,
              },
              roles: [],
            })
          : undefined,
      ...params,
    },
  }
}

/**
 * Print log to console
 */
const printToConsole = (logObject: LogObject): void => {
  /* eslint-disable no-console */
  const prefix = `${logObject.createdAt} ${logObject.level.toUpperCase()}`
  if (logObject.level === LogLevel.error) {
    console.error(`${prefix} ${logObject.message}`, logObject.params)
  } else if (logObject.level === LogLevel.debug) {
    console.debug(`${prefix} ${logObject.message}`, logObject.params)
  } else {
    console.log(`${prefix} ${logObject.message}`, logObject.params)
  }
  /* eslint-enable no-console */
}

/**
 * Log message
 */
const log = async (logParams: LogParams): Promise<boolean> => {
  const { sendQueuedMessages = false } = logParams

  if (!url) {
    return false
  }

  const logObject = buildLogObject(logParams)

  // Always print to console
  printToConsole(logObject)

  // Only send to server in demo mode
  if (!isDemoMode) {
    return true
  }

  const authToken = getToken()
  if (!authToken) {
    addLogToQueue(logObject)
    return false
  }

  try {
    await apiClient.post(url, logObject)

    if (sendQueuedMessages) {
      sendQueuedLogs()
    }

    return true
  } catch (error: unknown) {
    const axiosError = error as { response?: { status: number } }
    // if offline - add log object to the queue
    if (!axiosError.response) {
      addLogToQueue(logObject)
    } else {
      /* eslint-disable no-console */
      console.error(error)
      /* eslint-enable no-console */
    }
    return false
  }
}

/**
 * Log debug message
 */
export const debug = (
  message: string,
  params?: Record<string, unknown>,
): void => {
  log({ level: LogLevel.debug, message, params, sendQueuedMessages: true })
}

/**
 * Log info message
 */
export const info = (
  message: string,
  params?: Record<string, unknown>,
): void => {
  log({ level: LogLevel.info, message, params, sendQueuedMessages: true })
}

/**
 * Log error message
 */
export const error = (
  message: string,
  params?: Record<string, unknown>,
): void => {
  log({ level: LogLevel.error, message, params, sendQueuedMessages: true })
}

export default {
  debug,
  info,
  error,
}
